import React, { useCallback, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';
import { GET_LIST, useDataProvider, useQueryWithStore, useRefresh } from 'react-admin';
import moment, { Moment } from 'moment';
import { get } from 'lodash';

import { Dialog } from 'components/common';
import { Input, Select, validator } from 'components/form';
import DateInputField from 'components/form/DateInputField';
import {
  CountryDTO,
  DeliveryInterceptDTO,
  ShipmentContactDTO,
  ShipmentDetailsDTO,
  ShipmentInterceptionType,
  ShipmentReasonForReturn,
} from '../../types';
import RerouteContactFields from './RerouteContactFields';
import { DictionaryStatus, InterceptionFormValues } from './types';

const useStyles = makeStyles((theme) => ({
  dialog: {
    width: theme.spacing(100),
    maxWidth: 'calc(100vw - 32px)',
  },
  dialogContent: {
    minWidth: 0,
    maxHeight: 'calc(100vh - 240px)',
    overflowY: 'auto',
  },
}));

const interceptionTypeOptions: {
  label: string;
  value: ShipmentInterceptionType;
}[] = [
  { label: 'Return to sender', value: 'RETURN_TO_SENDER' },
  { label: 'Reroute to different address', value: 'REROUTE_TO_DIFFERENT_ADDRESS' },
  { label: 'Reschedule delivery', value: 'RESCHEDULE_DELIVERY' },
  { label: 'Hold at facility', value: 'HOLD_AT_FACILITY' },
];

const reasonForReturnOptions: {
  label: string;
  value: ShipmentReasonForReturn;
}[] = [
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Duplicate', value: 'DUPLICATE' },
  { label: 'No COD', value: 'NO_COD' },
  { label: 'Too expensive', value: 'TOO_EXPENSIVE' },
  { label: 'Shipped too late', value: 'SHIPPED_TOO_LATE' },
  { label: 'Bad address', value: 'BAD_ADDRESS' },
  { label: 'Refused', value: 'REFUSED' },
  { label: 'Not ordered', value: 'NOT_ORDERED' },
  { label: 'Other', value: 'OTHER' },
];

const getInitialDeliveryAddress = (record?: ShipmentDetailsDTO): ShipmentContactDTO => {
  const destination = get(record, 'params.to', {});
  const address = get(destination, 'address', {});
  const phone = get(destination, 'phone');
  const clientName = [
    get(destination, 'client.firstName') || get(destination, 'firstName'),
    get(destination, 'client.lastName') || get(destination, 'lastName'),
  ]
    .filter(Boolean)
    .join(' ');

  return {
    name: get(destination, 'name') || get(destination, 'fullName') || clientName,
    companyName: get(destination, 'companyName') || '',
    taxIdentificationNumber: get(destination, 'taxIdentificationNumber') || '',
    phoneNumber:
      get(destination, 'phoneNumber') ||
      get(destination, 'phone.number') ||
      (typeof phone === 'string' ? phone : ''),
    emailAddress: get(destination, 'emailAddress') || get(destination, 'email') || '',
    address: {
      addressLine1: get(address, 'addressLine1') || '',
      addressLine2: get(address, 'addressLine2') || '',
      addressLine3: get(address, 'addressLine3') || '',
      city: get(address, 'city') || get(destination, 'city') || '',
      stateProvinceCode:
        get(address, 'stateProvinceCode') ||
        get(address, 'state') ||
        get(destination, 'stateProvinceCode') ||
        '',
      zipCode:
        get(address, 'zipCode') ||
        get(address, 'postalCode') ||
        get(destination, 'postalCode') ||
        '',
      countryCode: get(address, 'countryCode') || get(destination, 'countryCode') || 'US',
      residential: Boolean(get(address, 'residential')),
    },
  };
};

const getInitialFormValues = (record?: ShipmentDetailsDTO): Partial<InterceptionFormValues> => {
  const values: Partial<InterceptionFormValues> = {
    type: record?.interceptionType,
    specialInstructions: '',
    newContact: getInitialDeliveryAddress(record),
  };

  if (record?.interceptionType === 'RETURN_TO_SENDER') {
    values.reasonForReturn = record.reasonForReturn;
  }

  return values;
};

const validateNewDeliveryDate = (value?: Moment) => {
  const requiredError = validator.required(value);

  if (requiredError) {
    return requiredError;
  }

  if (!moment(value).isValid()) {
    return 'Invalid date';
  }

  return moment(value).startOf('day').isBefore(moment().startOf('day'))
    ? 'Date cannot be in the past'
    : undefined;
};

const getErrorMessage = (error: any) =>
  typeof error === 'string' ? error : error?.message || 'Unable to load address dictionaries';

const InterceptionDialog = ({
  record,
  onClose,
}: {
  record?: ShipmentDetailsDTO;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const initialValues = useMemo(() => getInitialFormValues(record), [record]);
  const [stateDictionaryStatus, setStateDictionaryStatus] = useState<DictionaryStatus>({
    loading: true,
  });
  const {
    data: countriesData,
    loading: countriesLoading,
    error: countriesError,
  } = useQueryWithStore({
    type: GET_LIST,
    resource: 'countries',
    payload: { pagination: { perPage: 999 } },
  });
  const countries: CountryDTO[] = Array.isArray(countriesData) ? countriesData : [];
  const handleStateStatusChange = useCallback((status: DictionaryStatus) => {
    setStateDictionaryStatus(status);
  }, []);

  const handleSubmit = async (values: InterceptionFormValues) => {
    try {
      if (!record?.id) {
        throw new Error('Shipment ID is required');
      }

      const submitValues: DeliveryInterceptDTO = {
        type: values.type,
        specialInstructions: values.specialInstructions,
      };

      if (values.type === 'RETURN_TO_SENDER') {
        submitValues.reasonForReturn = values.reasonForReturn;
      }

      if (values.type === 'RESCHEDULE_DELIVERY' && values.newDeliveryDate) {
        submitValues.newDeliveryDate = values.newDeliveryDate.toISOString();
      }

      if (values.type === 'REROUTE_TO_DIFFERENT_ADDRESS' && values.newContact) {
        const address = values.newContact.address;

        submitValues.newContact = {
          name: values.newContact.name,
          companyName: values.newContact.companyName,
          taxIdentificationNumber: values.newContact.taxIdentificationNumber,
          phone: values.newContact.phoneNumber,
          emailAddress: values.newContact.emailAddress,
          address: {
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            addressLine3: address.addressLine3,
            city: address.city,
            stateProvinceCode: address.stateProvinceCode,
            zipCode: address.zipCode,
            countryCode: address.countryCode,
            residential: Boolean(address.residential),
          },
        };
      }

      await dataProvider.post('shipmentInterception', {
        id: record.id,
        data: submitValues,
      });

      refresh();
      onClose();
    } catch (error: any) {
      return { [FORM_ERROR]: error?.message ?? error };
    }
  };

  return (
    <Form<InterceptionFormValues>
      initialValues={initialValues}
      destroyOnUnregister
      onSubmit={handleSubmit}
      render={({ handleSubmit, submitting, submitError, values }) => {
        const isReroute = values.type === 'REROUTE_TO_DIFFERENT_ADDRESS';
        const countryCode = get(values, 'newContact.address.countryCode');
        const selectedCountry = countries.find((country) => country.code === countryCode);
        const stateRequired = countryCode === 'US' || countryCode === 'CA';
        const statesUnavailable =
          isReroute &&
          Boolean(
            countryCode &&
              (!selectedCountry ||
                (stateRequired && !selectedCountry.id) ||
                (selectedCountry.id &&
                  (stateDictionaryStatus.countryId !== selectedCountry.id ||
                    stateDictionaryStatus.loading ||
                    stateDictionaryStatus.error)))
          );
        const dictionariesUnavailable =
          isReroute && Boolean(countriesLoading || countriesError || statesUnavailable);
        const dictionaryError = isReroute
          ? countriesError
            ? getErrorMessage(countriesError)
            : countryCode && !selectedCountry
              ? 'Selected country is unavailable'
              : stateRequired && selectedCountry && !selectedCountry.id
                ? 'State dictionary is unavailable for the selected country'
                : stateDictionaryStatus.error
                  ? getErrorMessage(stateDictionaryStatus.error)
                  : undefined
          : undefined;
        const handleGuardedSubmit = (event?) => {
          if (dictionariesUnavailable) {
            event?.preventDefault();
            event?.stopPropagation();
            return;
          }

          return handleSubmit(event);
        };

        return (
          <form onSubmit={handleGuardedSubmit}>
            <Dialog
              open
              title="Requests UPS delivery interception"
              cancelText="Close"
              confirmText="Submit"
              submitting={submitting}
              error={submitError || dictionaryError}
              onCancel={onClose}
              onConfirm={handleGuardedSubmit}
              classes={{ content: classes.dialogContent, root: classes.dialog }}>
              <Select
                name="type"
                label="Interception type"
                options={interceptionTypeOptions}
                validate={validator.required}
              />
              {values.type === 'RETURN_TO_SENDER' && (
                <Select
                  name="reasonForReturn"
                  label="Reason for return"
                  options={reasonForReturnOptions}
                  validate={validator.required}
                />
              )}
              {values.type === 'RESCHEDULE_DELIVERY' && (
                <DateInputField
                  name="newDeliveryDate"
                  label="New delivery date"
                  textField
                  validate={validateNewDeliveryDate}
                  pickerProps={{
                    disablePast: true,
                    views: ['year', 'month', 'date'],
                  }}
                />
              )}
              {isReroute && (
                <RerouteContactFields
                  countries={countries}
                  countriesLoading={countriesLoading}
                  countriesError={countriesError}
                  onStateStatusChange={handleStateStatusChange}
                />
              )}
              <Input
                name="specialInstructions"
                label="Special instructions"
                inputProps={{ multiline: true, rows: 4 }}
              />
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default InterceptionDialog;
